import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.css";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// Formik
import * as Yup from "yup";
import { useFormik } from "formik";

import { Loader } from "../../Common/Loader";
import { X, Loader as LoaderIcon } from "lucide-react";
import { Common } from "helpers/common";
import { ToastContainer } from "react-toastify";
import DeleteModal from "Common/DeleteModal";
import moment from "moment";
import Drawer from "Common/Components/Drawer";
import { addTeacher, updateTeacher } from "helpers/backend_helper";
import { useAuth } from "hooks/useStore";

type Props = { onHide: () => void; show: boolean, data: any; onSave: (result: any) => void };
export const AddTeacher: React.FC<Props> = ({ onHide, show, data, onSave }) => {

  const { id } = useParams();
  const navigate = useNavigate();
  const [teacherData, setTeacherData] = useState<any>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);

  const { user } = useAuth();
  // Delete Modal
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const deleteToggle = () => setDeleteModal(!deleteModal);

  const uiHelper = new Common();

  const salaryTypeOptions = [
    { value: "1", label: "Hourly" },
    { value: "2", label: "Monthly" },
    // { value: "3", label: "Annual" }
  ];

  const stateOptions = [
    { value: "AL", label: "Alabama" },
    { value: "AK", label: "Alaska" },
    { value: "AZ", label: "Arizona" },
    { value: "AR", label: "Arkansas" },
    { value: "CA", label: "California" },
    { value: "CO", label: "Colorado" },
    { value: "CT", label: "Connecticut" },
    { value: "DE", label: "Delaware" },
    { value: "FL", label: "Florida" },
    { value: "GA", label: "Georgia" },
    { value: "HI", label: "Hawaii" },
    { value: "ID", label: "Idaho" },
    { value: "IL", label: "Illinois" },
    { value: "IN", label: "Indiana" },
    { value: "IA", label: "Iowa" },
    { value: "KS", label: "Kansas" },
    { value: "KY", label: "Kentucky" },
    { value: "LA", label: "Louisiana" },
    { value: "ME", label: "Maine" },
    { value: "MD", label: "Maryland" },
    { value: "MA", label: "Massachusetts" },
    { value: "MI", label: "Michigan" },
    { value: "MN", label: "Minnesota" },
    { value: "MS", label: "Mississippi" },
    { value: "MO", label: "Missouri" },
    { value: "MT", label: "Montana" },
    { value: "NE", label: "Nebraska" },
    { value: "NV", label: "Nevada" },
    { value: "NH", label: "New Hampshire" },
    { value: "NJ", label: "New Jersey" },
    { value: "NM", label: "New Mexico" },
    { value: "NY", label: "New York" },
    { value: "NC", label: "North Carolina" },
    { value: "ND", label: "North Dakota" },
    { value: "OH", label: "Ohio" },
    { value: "OK", label: "Oklahoma" },
    { value: "OR", label: "Oregon" },
    { value: "PA", label: "Pennsylvania" },
    { value: "RI", label: "Rhode Island" },
    { value: "SC", label: "South Carolina" },
    { value: "SD", label: "South Dakota" },
    { value: "TN", label: "Tennessee" },
    { value: "TX", label: "Texas" },
    { value: "UT", label: "Utah" },
    { value: "VT", label: "Vermont" },
    { value: "VA", label: "Virginia" },
    { value: "WA", label: "Washington" },
    { value: "WV", label: "West Virginia" },
    { value: "WI", label: "Wisconsin" },
    { value: "WY", label: "Wyoming" }
  ];

  useEffect(() => {
    if (data?.teacherId) {
      setIsEdit(true);
      loadTeacher();
    } else {
      setIsEdit(false);
      setTeacherData({});
      validation.resetForm();
    }
  }, [data]);

  const loadTeacher = async () => {
    setIsLoading(true);
    try {
      setTeacherData(data);

      // Set form values
      validation.setValues({
        teacherId: data.teacherId,
        clientId: data.clientId || 1,
        firstName: data.firstName,
        lastName: data.lastName,
        streetAddress: data.streetAddress || '',
        address1: data.address1 || '',
        city: data.city || '',
        state: data.state || '',
        zip: data.zip || '',
        phone: data.phone || '',
        email: data.email,
        salaryType: data.salaryType || 1,
        rate: data.rate || 0,
        joinedOn: data.joinedOn ? moment(data.joinedOn).format("YYYY-MM-DD") : ""
      });
    } catch (error) {
      console.error("Error loading teacher:", error);
      uiHelper.showErrorMessage("Error loading teacher data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDrawer = () => { onHide(); }

  // validation
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      teacherId: (data && data.teacherId) || 0,
      clientId: (data && data.clientId) || 1,
      firstName: (data && data.firstName) || "",
      lastName: (data && data.lastName) || "",
      streetAddress: (data && data.streetAddress) || "",
      address1: (data && data.address1) || "",
      city: (data && data.city) || "",
      state: (data && data.state) || "",
      zip: (data && data.zip) || "",
      phone: (data && data.phone) || "",
      email: (data && data.email) || "",
      salaryType: (data && data.salaryType) || "Hourly",
      rate: (data && data.rate) || 0,
      joinedOn: (data && data.joinedOn) ? moment(data.joinedOn).format("YYYY-MM-DD") : ""
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required("Please enter first name"),
      lastName: Yup.string().required("Please enter last name"),
      email: Yup.string().email("Please enter a valid email").required("Please enter email"),
      phone: Yup.string()
        .required("Please enter phone number")
        .matches(/^\d{10}$/, "Phone number must be exactly 10 digits")
        .length(10, "Phone number must be exactly 10 digits"),
      city: Yup.string().required("Please enter city"),
      state: Yup.string().required("Please select state"),
      zip: Yup.string().required("Please enter zip code"),
      salaryType: Yup.string().required("Please select salary type"),
      rate: Yup.number().min(0, "Rate must be greater than or equal to 0").required("Please enter rate"),
      joinedOn: Yup.string().required("Please select joined date")
    }),
    onSubmit: (values) => {
      handleSave(values);
    }
  });

  const handleSave = async (values: any) => {
    setIsSaving(true);
    try {
      const formData = {
        ...values,
        createdBy: user?.userId ?? 1,
        updatedBy: user?.userId ?? 1
      };

      let response;
      if (isEdit && values.teacherId) {
        // Update existing teacher
        response = await updateTeacher(formData);
      } else {
        // Create new teacher
        response = await addTeacher(formData);
      }
      
      uiHelper.showSuccessMessage(
        `Teacher has been successfully ${isEdit ? 'updated' : 'saved'}.`
      );

      onSave(response.data);
      //onHide();
    } catch (error) {
      console.error("Error saving teacher:", error);
      uiHelper.showErrorMessage("Error saving teacher data");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    onHide();
  };

  return (
    <React.Fragment>
      <Drawer show={show} onHide={handleDrawer} id="teacherSidePenal" drawer-end="true" className="fixed inset-y-0 flex flex-col  
      transition-transform duration-600 ease-in-out transform bg-white shadow 
     dark:bg-zink-600 ltr:right-0 rtl:left-0  w-5/6 flex  z-drawer">

        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-zink-500">
          <div className="grow">
            <h5 className="mb-0 text-16">{isEdit ? "Edit" : "Add"} Teacher</h5>
          </div>
          <div className="shrink-0">
            <div className="flex items-center justify-end gap-[200px]">
              <div className="flex items-center gap-2 group">

              </div>
              <Drawer.Header data-drawer-close="teacherSidePenal" className="transition-all duration-150 ease-linear text-slate-500 hover:text-slate-800">
                <X className="size-4"></X>
              </Drawer.Header>
            </div>
          </div>
        </div>

        <Drawer.Body className="h-full p-0 overflow-y-auto">
          <div className="flex h-full">
            {/* Form Section - 100% width */}
            <div className="w-full h-full overflow-y-auto">
              <div className="h-full overflow-y-auto product-list">
                <div className="row">
                  <div className="col-xl-12">
                    <div className="card">
                      <div className="card-body">
                        {isLoading ? (
                          <Loader />
                        ) : (
                          <form
                            id="teacherForm"
                            className="needs-validation"
                            onSubmit={(e) => {
                              e.preventDefault();
                              validation.handleSubmit();
                              return false;
                            }}
                          >
                            <div className="p-4">
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                
                                {/* LEFT PANEL - Personal & Contact Information */}
                                <div className="space-y-6">
                                  
                                  {/* Personal Information Section */}
                                  <div className="bg-slate-50 dark:bg-zink-600 p-4 rounded-lg">
                                    <div className="mb-4">
                                      <h6 className="text-15 font-semibold text-slate-700 dark:text-zink-100 bg-white dark:bg-zink-700 px-3 py-2 rounded-md shadow-sm border border-slate-200 dark:border-zink-500">
                                        👨‍🏫 Personal Information
                                      </h6>
                                    </div>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <div>
                                        <label className="form-label" htmlFor="firstName">
                                          First Name <span className="text-danger">*</span>
                                        </label>
                                        <input
                                          id="firstName"
                                          name="firstName"
                                          type="text"
                                          className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 ${validation.touched.firstName && validation.errors.firstName ? "is-invalid" : ""}`}
                                          placeholder="Enter first name"
                                          onChange={validation.handleChange}
                                          onBlur={validation.handleBlur}
                                          value={validation.values.firstName}
                                        />
                                        {validation.touched.firstName && validation.errors.firstName ? (
                                          <div className="mt-1 text-sm text-red-500">
                                            {validation.errors.firstName?.toString()}
                                          </div>
                                        ) : null}
                                      </div>

                                      <div>
                                        <label className="form-label" htmlFor="lastName">
                                          Last Name <span className="text-danger">*</span>
                                        </label>
                                        <input
                                          id="lastName"
                                          name="lastName"
                                          type="text"
                                          className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 ${validation.touched.lastName && validation.errors.lastName ? "is-invalid" : ""}`}
                                          placeholder="Enter last name"
                                          onChange={validation.handleChange}
                                          onBlur={validation.handleBlur}
                                          value={validation.values.lastName}
                                        />
                                        {validation.touched.lastName && validation.errors.lastName ? (
                                          <div className="mt-1 text-sm text-red-500">
                                            {validation.errors.lastName?.toString()}
                                          </div>
                                        ) : null}
                                      </div>

                                      <div>
                                        <label className="form-label" htmlFor="email">
                                          Email <span className="text-danger">*</span>
                                        </label>
                                        <input
                                          id="email"
                                          name="email"
                                          type="email"
                                          className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 ${validation.touched.email && validation.errors.email ? "is-invalid" : ""}`}
                                          placeholder="Enter email address"
                                          onChange={validation.handleChange}
                                          onBlur={validation.handleBlur}
                                          value={validation.values.email}
                                        />
                                        {validation.touched.email && validation.errors.email ? (
                                          <div className="mt-1 text-sm text-red-500">
                                            {validation.errors.email?.toString()}
                                          </div>
                                        ) : null}
                                      </div>

                                      <div>
                                        <label className="form-label" htmlFor="phone">
                                          Phone <span className="text-danger">*</span>
                                        </label>
                                        <input
                                          id="phone"
                                          name="phone"
                                          type="text"
                                          maxLength={10}
                                          className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 ${validation.touched.phone && validation.errors.phone ? "is-invalid" : ""}`}
                                          placeholder="Enter phone number"
                                          onChange={validation.handleChange}
                                          onBlur={validation.handleBlur}
                                          value={validation.values.phone}
                                        />
                                        {validation.touched.phone && validation.errors.phone ? (
                                          <div className="mt-1 text-sm text-red-500">
                                            {validation.errors.phone?.toString()}
                                          </div>
                                        ) : null}
                                      </div>

                                      <div>
                                        <label className="form-label" htmlFor="joinedOn">
                                          Joined Date <span className="text-danger">*</span>
                                        </label>
                                        <Flatpickr
                                          id="joinedOn"
                                          name="joinedOn"
                                          className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 ${validation.touched.joinedOn && validation.errors.joinedOn ? "is-invalid" : ""}`}
                                          placeholder="Select joined date"
                                          options={{
                                            dateFormat: "Y-m-d",
                                            defaultDate: validation.values.joinedOn || undefined
                                          }}
                                          onChange={(date: any) => {
                                            validation.setFieldValue(
                                              "joinedOn",
                                              date.length > 0 ? moment(date[0]).format("YYYY-MM-DD") : ""
                                            );
                                          }}
                                          value={validation.values.joinedOn || undefined}
                                        />
                                        {validation.touched.joinedOn && validation.errors.joinedOn ? (
                                          <div className="mt-1 text-sm text-red-500">
                                            {validation.errors.joinedOn?.toString()}
                                          </div>
                                        ) : null}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Address Information Section */}
                                  <div className="bg-slate-50 dark:bg-zink-600 p-4 rounded-lg">
                                    <div className="mb-4">
                                      <h6 className="text-15 font-semibold text-slate-700 dark:text-zink-100 bg-white dark:bg-zink-700 px-3 py-2 rounded-md shadow-sm border border-slate-200 dark:border-zink-500">
                                        📍 Address Information
                                      </h6>
                                    </div>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <div>
                                        <label className="form-label" htmlFor="streetAddress">
                                          Street Address
                                        </label>
                                        <input
                                          id="streetAddress"
                                          name="streetAddress"
                                          type="text"
                                          className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                          placeholder="Enter street address"
                                          onChange={validation.handleChange}
                                          onBlur={validation.handleBlur}
                                          value={validation.values.streetAddress}
                                        />
                                      </div>

                                      <div>
                                        <label className="form-label" htmlFor="address1">
                                          Address Line 2
                                        </label>
                                        <input
                                          id="address1"
                                          name="address1"
                                          type="text"
                                          className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                          placeholder="Enter address line 2"
                                          onChange={validation.handleChange}
                                          onBlur={validation.handleBlur}
                                          value={validation.values.address1}
                                        />
                                      </div>

                                      <div>
                                        <label className="form-label" htmlFor="city">
                                          City <span className="text-danger">*</span>
                                        </label>
                                        <input
                                          id="city"
                                          name="city"
                                          type="text"
                                          className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 ${validation.touched.city && validation.errors.city ? "is-invalid" : ""}`}
                                          placeholder="Enter city"
                                          onChange={validation.handleChange}
                                          onBlur={validation.handleBlur}
                                          value={validation.values.city}
                                        />
                                        {validation.touched.city && validation.errors.city ? (
                                          <div className="mt-1 text-sm text-red-500">
                                            {validation.errors.city?.toString()}
                                          </div>
                                        ) : null}
                                      </div>

                                      <div>
                                        <label className="form-label" htmlFor="state">
                                          State <span className="text-danger">*</span>
                                        </label>
                                        <select
                                          id="state"
                                          name="state"
                                          className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 ${validation.touched.state && validation.errors.state ? "is-invalid" : ""}`}
                                          onChange={validation.handleChange}
                                          onBlur={validation.handleBlur}
                                          value={validation.values.state}
                                        >
                                          <option value="">Select state</option>
                                          {stateOptions.map((option) => (
                                            <option key={option.value} value={option.value}>
                                              {option.label}
                                            </option>
                                          ))}
                                        </select>
                                        {validation.touched.state && validation.errors.state ? (
                                          <div className="mt-1 text-sm text-red-500">
                                            {validation.errors.state?.toString()}
                                          </div>
                                        ) : null}
                                      </div>

                                      <div>
                                        <label className="form-label" htmlFor="zip">
                                          Zip Code <span className="text-danger">*</span>
                                        </label>
                                        <input
                                          id="zip"
                                          name="zip"
                                          type="text"
                                          className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 ${validation.touched.zip && validation.errors.zip ? "is-invalid" : ""}`}
                                          placeholder="Enter zip code"
                                          onChange={validation.handleChange}
                                          onBlur={validation.handleBlur}
                                          value={validation.values.zip}
                                        />
                                        {validation.touched.zip && validation.errors.zip ? (
                                          <div className="mt-1 text-sm text-red-500">
                                            {validation.errors.zip?.toString()}
                                          </div>
                                        ) : null}
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* RIGHT PANEL - Employment Information */}
                                <div className="space-y-6">
                                  
                                  {/* Employment Information Section */}
                                  <div className="bg-slate-50 dark:bg-zink-600 p-4 rounded-lg">
                                    <div className="mb-4">
                                      <h6 className="text-15 font-semibold text-slate-700 dark:text-zink-100 bg-white dark:bg-zink-700 px-3 py-2 rounded-md shadow-sm border border-slate-200 dark:border-zink-500">
                                        💼 Employment Information
                                      </h6>
                                    </div>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <div>
                                        <label className="form-label" htmlFor="salaryType">
                                          Salary Type <span className="text-danger">*</span>
                                        </label>
                                        <select
                                          id="salaryType"
                                          name="salaryType"
                                          className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 ${validation.touched.salaryType && validation.errors.salaryType ? "is-invalid" : ""}`}
                                          onChange={validation.handleChange}
                                          onBlur={validation.handleBlur}
                                          value={validation.values.salaryType}
                                        >
                                          <option value="">Select salary type</option>
                                          {salaryTypeOptions.map((option) => (
                                            <option key={option.value} value={option.value}>
                                              {option.label}
                                            </option>
                                          ))}
                                        </select>
                                        {validation.touched.salaryType && validation.errors.salaryType ? (
                                          <div className="mt-1 text-sm text-red-500">
                                            {validation.errors.salaryType?.toString()}
                                          </div>
                                        ) : null}
                                      </div>

                                      <div>
                                        <label className="form-label" htmlFor="rate">
                                          {validation.values.salaryType === "1" ? "Hourly Rate" : "Monthly Rate"} <span className="text-danger">*</span>
                                        </label>
                                        <input
                                          id="rate"
                                          name="rate"
                                          type="number"
                                          step="0.01"
                                          className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 ${validation.touched.rate && validation.errors.rate ? "is-invalid" : ""}`}
                                          placeholder={validation.values.salaryType === "1" ? "Enter hourly rate" : "Enter monthly rate"}
                                          onChange={validation.handleChange}
                                          onBlur={validation.handleBlur}
                                          value={validation.values.rate}
                                        />
                                        {validation.touched.rate && validation.errors.rate ? (
                                          <div className="mt-1 text-sm text-red-500">
                                            {validation.errors.rate?.toString()}
                                          </div>
                                        ) : null}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </form>
                        )}
                        <ToastContainer closeButton={false} limit={1} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Drawer.Body>
        
        <Drawer.Footer className="flex items-center justify-end p-4 border-t border-slate-200 dark:border-zink-500 bg-white dark:bg-zink-600">
          <div className="flex gap-2">
            <button
              type="button"
              className="text-slate-500 btn bg-white border-slate-200 hover:text-slate-600 hover:bg-slate-100 hover:border-slate-200 focus:text-slate-600 focus:bg-slate-100 focus:border-slate-200 focus:ring focus:ring-slate-100 active:text-slate-600 active:bg-slate-100 active:border-slate-200 active:ring active:ring-slate-100 dark:bg-zink-700 dark:hover:bg-zink-600 dark:border-zink-500 dark:text-zink-200"
              onClick={handleCancel}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="teacherForm"
              className="text-white transition-all duration-200 ease-linear btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <LoaderIcon className="size-4 ltr:mr-2 rtl:ml-2 animate-spin" />
                  Saving...
                </>
              ) : "Save"}
            </button>
          </div>
        </Drawer.Footer>
      </Drawer>

      {/* Delete Modal */}
      <DeleteModal
        show={deleteModal}
        onHide={deleteToggle}
        onDelete={() => {}}
        buttonCaption="Yes, Delete It!"
      />
    </React.Fragment>
  );
}

export default AddTeacher;
